/**
 * Scenario Comparison API Hook
 * Provides data fetching for comparing AI plan vs original LN plan
 */

import { useQuery } from '@tanstack/react-query';
import api from '../api/client';

const API_BASE = '/planning';

// Types for comparison data
export interface OperationComparison {
  order_number: string;
  operation_sequence: number;
  description: string;
  original: {
    start: string | null;
    end: string | null;
    work_center: string | null;
  };
  planned: {
    start: string;
    end: string;
    work_center: string | null;
  };
  delta: {
    start_hours: number | null;
    end_hours: number | null;
    work_center_changed: boolean | null;
  };
  ai_reasoning: string | null;
}

export interface ComparisonSummary {
  total_operations: number;
  moved_earlier: number;
  moved_later: number;
  unchanged: number;
  work_center_changes: number;
}

export interface ComparisonResponse {
  scenario_id: string;
  scenario_name: string;
  comparison: OperationComparison[];
  summary: ComparisonSummary;
}

// Fetch comparison data for a scenario
async function fetchComparisonData(scenarioId: string): Promise<ComparisonResponse> {
  const response = await api.get(`${API_BASE}/scenarios/${scenarioId}/compare/`);
  return response.data;
}

// Hook: Fetch scenario comparison
export function useScenarioComparison(
  scenarioId: string | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: ['scenario-comparison', scenarioId],
    queryFn: () => fetchComparisonData(scenarioId!),
    enabled: enabled && !!scenarioId,
    refetchInterval: 60000, // Refresh every minute
  });
}

// Helper: Get delta color class
export function getDeltaColor(hours: number | null): {
  bg: string;
  text: string;
  label: string;
} {
  if (hours === null || hours === 0) {
    return {
      bg: 'bg-gray-100',
      text: 'text-gray-600',
      label: 'Unchanged',
    };
  }
  if (hours < 0) {
    // Moved earlier (positive - earlier delivery)
    if (hours <= -24) {
      return {
        bg: 'bg-green-100',
        text: 'text-green-700',
        label: `${Math.abs(hours).toFixed(0)}h earlier`,
      };
    }
    return {
      bg: 'bg-green-50',
      text: 'text-green-600',
      label: `${Math.abs(hours).toFixed(1)}h earlier`,
    };
  }
  // Moved later (negative - later delivery)
  if (hours >= 24) {
    return {
      bg: 'bg-red-100',
      text: 'text-red-700',
      label: `${hours.toFixed(0)}h later`,
    };
  }
  return {
    bg: 'bg-red-50',
    text: 'text-red-600',
    label: `${hours.toFixed(1)}h later`,
  };
}

// Helper: Calculate comparison statistics
export function calculateComparisonStats(comparison: OperationComparison[]) {
  let totalDeltaHours = 0;
  let maxEarlier = 0;
  let maxLater = 0;
  let workCenterChanges = 0;

  for (const op of comparison) {
    if (op.delta.start_hours !== null) {
      totalDeltaHours += op.delta.start_hours;
      if (op.delta.start_hours < maxEarlier) {
        maxEarlier = op.delta.start_hours;
      }
      if (op.delta.start_hours > maxLater) {
        maxLater = op.delta.start_hours;
      }
    }
    if (op.delta.work_center_changed) {
      workCenterChanges++;
    }
  }

  return {
    averageDeltaHours: comparison.length > 0 ? totalDeltaHours / comparison.length : 0,
    maxEarlierHours: Math.abs(maxEarlier),
    maxLaterHours: maxLater,
    workCenterChanges,
    totalOperations: comparison.length,
  };
}

// Helper: Group comparisons by order
export function groupComparisonsByOrder(
  comparison: OperationComparison[]
): Map<string, OperationComparison[]> {
  const grouped = new Map<string, OperationComparison[]>();

  for (const op of comparison) {
    if (!grouped.has(op.order_number)) {
      grouped.set(op.order_number, []);
    }
    grouped.get(op.order_number)!.push(op);
  }

  // Sort operations within each order by sequence
  for (const ops of grouped.values()) {
    ops.sort((a, b) => a.operation_sequence - b.operation_sequence);
  }

  return grouped;
}

// Helper: Filter comparisons by delta type
export function filterComparisonsByDelta(
  comparison: OperationComparison[],
  filter: 'all' | 'earlier' | 'later' | 'unchanged' | 'work_center_changed'
): OperationComparison[] {
  switch (filter) {
    case 'earlier':
      return comparison.filter((op) => op.delta.start_hours !== null && op.delta.start_hours < 0);
    case 'later':
      return comparison.filter((op) => op.delta.start_hours !== null && op.delta.start_hours > 0);
    case 'unchanged':
      return comparison.filter((op) => op.delta.start_hours === null || op.delta.start_hours === 0);
    case 'work_center_changed':
      return comparison.filter((op) => op.delta.work_center_changed === true);
    default:
      return comparison;
  }
}
